import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";

import FormSelectionField from "../../components/formFields/FormSelectionField";

import { STATUSARRAY } from "../../_contstants/common";
import { clearOpeningDetails } from "./openingsSlice";
import {
  createOpening,
  getOpeningByID,
  updateOpening,
} from "./services/openings.services";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormRichTextField from "../../components/formFields/FormRichTextField";

const CreateOpening: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const OpeningID = location.state?.OpeningID;
  const OpeningDetail = useAppSelector((state) => state.opening.OpeningDetail);
  const loading = useAppSelector((state) => state.opening.loading);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (OpeningID) {
      dispatch(getOpeningByID(OpeningID));
    }
    return () => {
      dispatch(clearOpeningDetails());
    };
  }, [OpeningID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [OpeningDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (OpeningID) {
        const editPayload = {
          ID: OpeningID,
          Profile: values?.Profile ?? "",
          Positions: values?.Positions ? `${values?.Positions}` : "0",
          Experience: values?.Experience ? `${values?.Experience}` : "0",
          Location: values?.Location ?? "",
          Eligibility: values?.Eligibility ?? "",
          Summary: values?.Summary ?? "",
          Responsibilities: values?.Responsibilities ?? "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateOpening(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate(-1);
        }
      } else {
        const insertPayload = {
          Profile: values?.Profile ?? "",
          Positions: values?.Positions ? `${values?.Positions}` : "0",
          Experience: values?.Experience ? `${values?.Experience}` : "0",
          Location: values?.Location ?? "",
          Eligibility: values?.Eligibility ?? "",
          Summary: values?.Summary ?? "",
          Responsibilities: values?.Responsibilities ?? "",
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createOpening(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/opening");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={OpeningDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={4}>
                    <Typography.h4>
                      {OpeningID ? "Update Opening" : "Create Opening"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Profile"
                      label="Profile"
                      placeholder="Profile"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Positions"
                      label="Positions"
                      placeholder="Positions"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Experience"
                      label="Experience"
                      placeholder="Experience"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Location"
                      label="Location"
                      placeholder="Location"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="Eligibility"
                      label="Eligibility"
                      placeholder="Eligibility"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="Summary"
                      label="Summary"
                      placeholder="Summary"
                      component={FormRichTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="Responsibilities"
                      label="Responsibilities"
                      placeholder="Responsibilities"
                      component={FormRichTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={OpeningID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />

                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/opening")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateOpening;
